// 参加ユーザー一覧（アバター付き）。PO Board の epic-presence-badges に近い構造。
// アバター画像が無い／取得失敗した場合はイニシャル表示でフォールバックする。
// アバターをクリックすると、その参加者の表示画面を追尾する「フォーカスモード」を開始する。

import { useEffect, useRef, useState } from "react";
import { getRemoteAvatarAllowedHttpsOrigins } from "../auth/avatar-image-url";
import { UserAvatar } from "./dashboard-shell/user-avatar";

interface Viewer {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarImageUrl?: string | undefined;
  readonly color: string;
}

export interface SelfColorConfigurationAnchor {
  readonly anchorElement: HTMLButtonElement;
  readonly restoreFocusElement: HTMLButtonElement;
}

interface Props {
  readonly viewers: readonly Viewer[];
  readonly maxVisible?: number | undefined;
  /** アバターのクリックで起動するフォーカスモード切り替えハンドラ。 */
  readonly onFocusViewer?: ((userId: string) => void) | undefined;
  /** 現在フォーカス中の userId（無ければ null）。アバターの選択スタイルを切り替えるのに使う。 */
  readonly focusedUserId?: string | null | undefined;
  /** 現在のログインユーザー ID。 */
  readonly currentUserId?: string | undefined;
  /** 古い presence snapshot より優先して自己アバターへ表示する現在色。 */
  readonly currentUserColor?: string | undefined;
  /** 自己アバターからマイカラー設定を開く。 */
  readonly onConfigureSelfColor?: ((anchor: SelfColorConfigurationAnchor) => void) | undefined;
  readonly selfColorPanelOpen?: boolean | undefined;
  readonly selfColorPanelId?: string | undefined;
}

export function PresenceBadges({
  viewers,
  maxVisible = 4,
  onFocusViewer,
  focusedUserId,
  currentUserId,
  currentUserColor,
  onConfigureSelfColor,
  selfColorPanelOpen = false,
  selfColorPanelId,
}: Props): JSX.Element {
  const visible = viewers.slice(0, maxVisible);
  const hidden = viewers.slice(maxVisible);
  const overflow = hidden.length;

  return (
    <div
      role="list"
      aria-label="参加ユーザー一覧"
      style={{
        display: "flex",
        padding: "4px",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: "9999px",
        boxShadow: "var(--shadow-1)",
      }}
    >
      {visible.length === 0 ? (
        <span style={{ padding: "4px 12px", fontSize: "12px", color: "var(--color-text-muted)" }}>
          自分のみ
        </span>
      ) : (
        visible.map((v, i) => (
          <PresenceAvatar
            key={v.userId}
            viewer={v}
            displayColor={resolvePresenceViewerColor(v, currentUserId, currentUserColor)}
            index={i}
            onFocus={v.userId === currentUserId ? undefined : onFocusViewer}
            onConfigureSelfColor={v.userId === currentUserId ? onConfigureSelfColor : undefined}
            focused={focusedUserId === v.userId}
            isSelf={v.userId === currentUserId}
            selfColorPanelOpen={selfColorPanelOpen}
            selfColorPanelId={selfColorPanelId}
          />
        ))
      )}
      {overflow > 0 ? (
        <PresenceOverflow
          hidden={hidden}
          overflow={overflow}
          onFocusViewer={onFocusViewer}
          focusedUserId={focusedUserId}
          currentUserId={currentUserId}
          currentUserColor={currentUserColor}
          onConfigureSelfColor={onConfigureSelfColor}
          selfColorPanelOpen={selfColorPanelOpen}
          selfColorPanelId={selfColorPanelId}
        />
      ) : null}
    </div>
  );
}

/** 自分だけは CurrentActor の色を優先し、遅れて届いた古い snapshot で巻き戻さない。 */
export function resolvePresenceViewerColor(
  viewer: Viewer,
  currentUserId: string | undefined,
  currentUserColor: string | undefined
): string {
  return viewer.userId === currentUserId && currentUserColor !== undefined
    ? currentUserColor
    : viewer.color;
}

// 参加者一覧が maxVisible を超えたときに表示する「+N」チップ。
// クリックで隠れている参加者の一覧ポップオーバーを開き、各参加者をフォーカスできるようにする。
// （従来は非対話の装飾のみで、省略された参加者をフォーカスする手段が無かった。要件⑩の改善。）
function PresenceOverflow({
  hidden,
  overflow,
  onFocusViewer,
  focusedUserId,
  currentUserId,
  currentUserColor,
  onConfigureSelfColor,
  selfColorPanelOpen,
  selfColorPanelId,
}: {
  readonly hidden: readonly Viewer[];
  readonly overflow: number;
  readonly onFocusViewer?: ((userId: string) => void) | undefined;
  readonly focusedUserId?: string | null | undefined;
  readonly currentUserId?: string | undefined;
  readonly currentUserColor?: string | undefined;
  readonly onConfigureSelfColor?: ((anchor: SelfColorConfigurationAnchor) => void) | undefined;
  readonly selfColorPanelOpen: boolean;
  readonly selfColorPanelId?: string | undefined;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [keepMountedForSelfPanel, setKeepMountedForSelfPanel] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!selfColorPanelOpen) setKeepMountedForSelfPanel(false);
  }, [selfColorPanelOpen]);

  // ポップオーバー表示中は、外側クリック / Escape で閉じる（ObjectContextMenu と同じ作法）。
  // チップ本体・ポップオーバーは rootRef 配下にあるため、その内側の pointerdown では閉じない。
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: PointerEvent | MouseEvent): void => {
      if (rootRef.current && rootRef.current.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // フォーカス選択後はポップオーバーを閉じる（選んで閉じる、メニュー的な挙動）。
  const focusFromPopover = (userId: string): void => {
    onFocusViewer?.(userId);
    setOpen(false);
  };

  const configureFromPopover = (anchor: SelfColorConfigurationAnchor): void => {
    // アンカーの実矩形をパネルが読み続けられるよう、一覧は視覚的に閉じても DOM だけ維持する。
    setKeepMountedForSelfPanel(true);
    setOpen(false);
    onConfigureSelfColor?.({
      anchorElement: anchor.anchorElement,
      restoreFocusElement: triggerRef.current ?? anchor.restoreFocusElement,
    });
  };

  const renderPopover = open || keepMountedForSelfPanel;

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-flex", marginLeft: "-8px" }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`さらに ${overflow} 人（クリックで一覧を表示）`}
        title={`さらに ${overflow} 人`}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          background: "var(--color-surface-muted)",
          border: "none",
          display: "grid",
          placeItems: "center",
          fontSize: "11px",
          fontWeight: 600,
          color: "var(--color-text)",
          cursor: "pointer",
          padding: 0,
        }}
      >
        +{overflow}
      </button>
      {renderPopover ? (
        <div
          role="list"
          aria-label="省略されている参加者"
          aria-hidden={!open}
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: "6px",
            zIndex: 20,
            minWidth: "180px",
            maxHeight: "50vh",
            overflowY: "auto",
            padding: "4px",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-2)",
            boxShadow: "var(--shadow-2)",
            visibility: open ? "visible" : "hidden",
            pointerEvents: open ? "auto" : "none",
          }}
        >
          {hidden.map((v) => {
            const isSelf = v.userId === currentUserId;
            return (
              // 行ラッパーには role を付けない（listitem のネストを避ける）。
              // 各行の listitem セマンティクスは内側の PresenceAvatar が担う。
              <div
                key={v.userId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "4px 8px",
                }}
              >
                <PresenceAvatar
                  viewer={v}
                  displayColor={resolvePresenceViewerColor(v, currentUserId, currentUserColor)}
                  index={0}
                  onFocus={isSelf ? undefined : focusFromPopover}
                  onConfigureSelfColor={isSelf ? configureFromPopover : undefined}
                  focused={focusedUserId === v.userId}
                  isSelf={isSelf}
                  selfColorPanelOpen={selfColorPanelOpen}
                  selfColorPanelId={selfColorPanelId}
                />
                <span
                  style={{
                    fontSize: "13px",
                    color: "var(--color-text)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {v.displayName}
                  {isSelf ? "（自分）" : ""}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function PresenceAvatar({
  viewer,
  displayColor,
  index,
  onFocus,
  onConfigureSelfColor,
  focused,
  isSelf,
  selfColorPanelOpen,
  selfColorPanelId,
}: {
  readonly viewer: Viewer;
  readonly displayColor: string;
  readonly index: number;
  readonly onFocus?: ((userId: string) => void) | undefined;
  readonly onConfigureSelfColor?: ((anchor: SelfColorConfigurationAnchor) => void) | undefined;
  readonly focused: boolean;
  readonly isSelf?: boolean | undefined;
  readonly selfColorPanelOpen: boolean;
  readonly selfColorPanelId?: string | undefined;
}): JSX.Element {
  // 他参加者はフォーカス、自分はマイカラー設定としてボタン化する。
  const interactive = typeof onFocus === "function" || typeof onConfigureSelfColor === "function";
  const sharedStyle = {
    position: "relative" as const,
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: `2px solid ${displayColor}`,
    marginLeft: index === 0 ? 0 : "-8px",
    background: "var(--color-surface-muted)",
    display: "grid" as const,
    placeItems: "center" as const,
    fontSize: "12px",
    fontWeight: 600,
    color: "var(--color-text)",
    overflow: "hidden" as const,
    boxShadow: focused ? `0 0 0 3px ${displayColor}` : undefined,
  };

  // 参加者一覧には自分自身も含まれる。リモート（他参加者）由来のアバターのみ https を
  // 信頼済み origin（IdP）へ限定し、任意 https による強制外部リクエスト/トラッキングを防ぐ。
  // 自分自身のアバターは従来どおり任意 https を許容し、自分の正規アイコンの退行を避ける。
  const allowedHttpsOrigins = isSelf ? undefined : getRemoteAvatarAllowedHttpsOrigins();
  const inner = (
    <UserAvatar
      alt={`${viewer.displayName} のアイコン`}
      allowedHttpsOrigins={allowedHttpsOrigins}
      className="presence-user-avatar"
      displayName={viewer.displayName}
      src={viewer.avatarImageUrl}
    />
  );

  if (!interactive) {
    // 自分自身のアバターは「自分」であることを示すツールチップに切り替える。
    const title = isSelf ? `${viewer.displayName}（自分）` : viewer.displayName;
    return (
      <div role="listitem" title={title} style={sharedStyle}>
        {inner}
      </div>
    );
  }

  // listitem ロールは外側のラッパーに持たせ、内側の <button> はネイティブの button
  // semantics（aria-pressed・キーボード操作・読み上げ）を保つ。隣接アバターの重なり
  // (marginLeft) はラッパー側で表現し、button 自体は marginLeft を 0 に上書きする。
  return (
    <span role="listitem" style={{ display: "inline-flex", marginLeft: index === 0 ? 0 : "-8px" }}>
      <button
        type="button"
        title={
          isSelf
            ? "自分のマイカラーを設定"
            : focused
              ? `${viewer.displayName} のフォーカスを解除`
              : `${viewer.displayName} の画面を追尾`
        }
        aria-label={
          isSelf
            ? "自分のマイカラーを設定"
            : focused
              ? `${viewer.displayName} のフォーカスを解除`
              : `${viewer.displayName} の画面を追尾`
        }
        {...(isSelf
          ? {
              "aria-haspopup": "dialog" as const,
              "aria-expanded": selfColorPanelOpen,
              ...(selfColorPanelId ? { "aria-controls": selfColorPanelId } : {}),
            }
          : { "aria-pressed": focused })}
        onClick={(event) => {
          if (isSelf && onConfigureSelfColor) {
            onConfigureSelfColor({
              anchorElement: event.currentTarget,
              restoreFocusElement: event.currentTarget,
            });
            return;
          }
          onFocus?.(viewer.userId);
        }}
        style={{
          ...sharedStyle,
          marginLeft: 0,
          padding: 0,
          cursor: "pointer",
        }}
      >
        {inner}
      </button>
    </span>
  );
}
